import { DateTime } from 'luxon';

import type { PortfolioItemGroupName } from '@savvy/constants/generated/gql-enums';

import type { ClientPortalTimeRange } from '../common/constants';

export type PortfolioHistoryGroupInput = {
  readonly name: PortfolioItemGroupName;
  readonly historicalValues: readonly { readonly date: string; readonly value: { readonly amount: string } }[];
};

export type NetWorthPoint = {
  readonly date: string;
  readonly amount: number;
};

export type NetWorthChange = {
  readonly amount: number;
  readonly percent: number | null;
};

const LIABILITY_GROUP_NAME: PortfolioItemGroupName = 'OTHER_LIABILITIES';

function groupSign(name: PortfolioItemGroupName) {
  return name === LIABILITY_GROUP_NAME ? -1 : 1;
}

function amountsByDate(group: PortfolioHistoryGroupInput) {
  const sign = groupSign(group.name);
  const byDate = new Map<string, number>();
  group.historicalValues.forEach(({ date, value }) => {
    const amount = Number(value.amount);
    byDate.set(date, (byDate.get(date) ?? 0) + (Number.isFinite(amount) ? sign * amount : 0));
  });
  return byDate;
}

export function buildNetWorthSeries(groups: readonly PortfolioHistoryGroupInput[]): NetWorthPoint[] {
  const groupsWithHistory = groups.filter((group) => group.historicalValues.length > 0);
  if (groupsWithHistory.length === 0) {
    return [];
  }

  const seriesByGroup = groupsWithHistory.map(amountsByDate);
  const dates = [...new Set(seriesByGroup.flatMap((byDate) => [...byDate.keys()]))].sort();
  const carried = seriesByGroup.map(() => 0);

  return dates.map((date) => {
    seriesByGroup.forEach((byDate, index) => {
      const amount = byDate.get(date);
      if (amount !== undefined) {
        carried[index] = amount;
      }
    });
    return { date, amount: carried.reduce((sum, amount) => sum + amount, 0) };
  });
}

function rangeStart(rangeId: ClientPortalTimeRange, latest: DateTime) {
  switch (rangeId) {
    case 'past_month':
      return latest.minus({ months: 1 });
    case 'past_three_months':
      return latest.minus({ months: 3 });
    case 'year_to_date':
      return latest.startOf('year');
    case 'past_year':
      return latest.minus({ years: 1 });
    case 'all_time':
      return null;
  }
}

export function sliceNetWorthSeries(series: readonly NetWorthPoint[], rangeId: ClientPortalTimeRange): NetWorthPoint[] {
  const last = series.at(-1);
  if (last === undefined) {
    return [];
  }

  const start = rangeStart(rangeId, DateTime.fromISO(last.date));
  if (start === null) {
    return [...series];
  }

  const withinRange = series.filter((point) => DateTime.fromISO(point.date) >= start);
  return withinRange.length > 0 ? withinRange : [last];
}

export function netWorthChange(series: readonly NetWorthPoint[]): NetWorthChange | null {
  const first = series.at(0);
  const last = series.at(-1);
  if (first === undefined || last === undefined || series.length < 2) {
    return null;
  }

  const amount = last.amount - first.amount;
  return { amount, percent: first.amount === 0 ? null : (amount / Math.abs(first.amount)) * 100 };
}

const DOMAIN_PAD_RATIO = 0.15;
const FLAT_SERIES_PAD_RATIO = 0.05;
const ZERO_SERIES_PAD = 1;

function domainPad(min: number, max: number) {
  const spread = max - min;
  if (spread > 0) {
    return spread * DOMAIN_PAD_RATIO;
  }
  const flatPad = Math.abs(max) * FLAT_SERIES_PAD_RATIO;
  return flatPad > 0 ? flatPad : ZERO_SERIES_PAD;
}

function paddedFloorNotCrossingZero(min: number, pad: number) {
  return min < 0 ? min - pad : Math.max(0, min - pad);
}

function paddedCeilingNotCrossingZero(max: number, pad: number) {
  return max >= 0 ? max + pad : Math.min(0, max + pad);
}

export function paddedDomain(points: readonly NetWorthPoint[]): [number, number] {
  const amounts = points.map((point) => point.amount);
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  const pad = domainPad(min, max);
  return [paddedFloorNotCrossingZero(min, pad), paddedCeilingNotCrossingZero(max, pad)];
}
